import {
  ApplicationConfig,
  provideZoneChangeDetection,
  importProvidersFrom,
  APP_INITIALIZER,
  inject,
  LOCALE_ID,
  DOCUMENT,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS,
  withXhr,
} from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';

import { routes } from './app.routes';
import { LanguageService } from './core/services/common/language.service';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { RateLimitInterceptor } from './core/interceptors/rate-limit.interceptor';
import { AccessDeniedInterceptor } from './core/interceptors/access-denied.interceptor';
import { TokenRefreshService } from './core/services/auth/token-refresh.service';
import { SessionBootstrapService } from './core/services/auth/session-bootstrap.service';
import { AccessRefreshService } from './core/services/access/access-refresh.service';
import { isSupportPage } from './core/services/support/support-paths';
import { initializeLegacyStorageCleanup } from './core/services/common/legacy-storage-cleanup.service';
import { Observable, catchError, map, of, timeout } from 'rxjs';
import { provideScKit } from './ui';

/** Tope del arranque de sesión: nunca dejar la app colgada esperando al API. */
const SESSION_BOOTSTRAP_TIMEOUT_MS = 15000;

// Register Spanish locale
registerLocaleData(localeEs);

// Factory function to initialize language
export function initializeLanguage() {
  const translateService = inject(TranslateService);
  const languageService = inject(LanguageService);

  return () => {
    const language = languageService.getBrowserLanguage();
    return translateService.use(language);
  };
}

// Factory function to initialize token refresh monitoring
export function initializeTokenRefresh() {
  const tokenRefreshService = inject(TokenRefreshService);

  return () => {
    tokenRefreshService.startTokenRefreshMonitoring();
  };
}

/**
 * Arranque con sesión guardada: `/auth/me` y DESPUÉS `/access/me`, antes de la
 * primera navegación (los guards y el menú dependen de los permisos). Sin
 * sesión no pide nada. Si falla o se demora, la app arranca igual: los
 * permisos quedan en 'error' (o se piden de nuevo desde el guard) y la
 * pantalla de inicio ofrece reintentar.
 *
 * En /support/start y /support/ended no se arranca nada: esas páginas arman
 * o cierran una sesión de soporte y la sesión normal de las otras pestañas no
 * se usa desde ellas.
 */
export function initializeSession() {
  const bootstrap = inject(SessionBootstrapService);
  const doc = inject(DOCUMENT);

  return (): Observable<void> | void => {
    if (isSupportPage(doc.location.pathname)) return;
    if (!bootstrap.hasStoredSession()) return;
    return bootstrap.load().pipe(
      timeout(SESSION_BOOTSTRAP_TIMEOUT_MS),
      map(() => undefined),
      catchError(() => of(undefined)),
    );
  };
}

/** Recarga de permisos sin navegar (pestaña visible, cada 5 min, tras un 403). */
export function initializeAccessRefresh() {
  const accessRefresh = inject(AccessRefreshService);

  return () => {
    accessRefresh.start();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideAnimations(),
    importProvidersFrom(TranslateModule.forRoot()),
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json',
    }),
    {
      provide: LOCALE_ID,
      useValue: 'es',
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RateLimitInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AccessDeniedInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLanguage,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTokenRefresh,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeSession,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAccessRefresh,
      multi: true,
    },
    // Borra las bases falsas `sc-hc-*` que el front dejó en localStorage.
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLegacyStorageCleanup,
      multi: true,
    },
    // Confirmaciones del kit (scConfirm) sin pasar el inyector.
    provideScKit(),
  ],
};
